/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';

export type DeployedModelUrlModalProps = BasicModalProps<object> & {
    predictUrl: string;
    submitCode: string;
    retrieveCode?: string;
    selectedResource?: string;
    isLoadingResources?: boolean;
    showDeploymentRegions?: boolean;
    resourceOptions?: IDropdownOption[];
    onChangeSelectedResource?: (resourceId: string) => void;
};

const messages = defineMessages({
    close: { id: 'modelDeploy.deployedModelUrlModal.close', defaultMessage: 'Close' },
    submitCode: { id: 'modelDeploy.deployedModelUrlModal.submitCode', defaultMessage: 'Submit code' },
    retrieveCode: { id: 'modelDeploy.deployedModelUrlModal.retrieveCode', defaultMessage: 'Retrieve code' },
    predictionURL: { id: 'modelDeploy.deployedModelUrlModal.predictionURL', defaultMessage: 'Prediction URL' },
    sampleRequest: { id: 'modelDeploy.deployedModelUrlModal.sampleRequest', defaultMessage: 'Sample request' },
    getEndpointModalTitle: { id: 'modelDeploy.deployedModelUrlModal.getEndpointModalTitle', defaultMessage: 'Get prediction URL' },
    getEndpointModelDesc: {
        id: 'modelDeploy.deployedModelUrlModal.getEndpointModelDesc',
        defaultMessage: "Use these sample requests as a starting point to call your model's endpoint"
    },
    deploymentRegion: {
        id: 'modelDeploy.deployedModelUrlModal.deploymentRegion',
        defaultMessage: 'Deployment region'
    },
    deploymentRegionPlaceholder: {
        id: 'modelDeploy.deployedModelUrlModal.deploymentRegionPlaceholder',
        defaultMessage: 'Select a deployment region'
    }
});

const { styleLabel, getClassName, styleComponent } = getStylistV2('DeployModelPage');
const ModalSubTitle = styleLabel('ModalSubTitle', { fontWeight: 'bold', marginBottom: 5, display: 'block' });
const SampleRequestSubTitle = styleComponent(ModalSubTitle)('SampleRequestSubTitle', { marginTop: 24 });
const predictionURLParagraphClassName = getClassName('predictionURLParagraphClassName', { marginBottom: 20 });
const textFieldStyles = (shouldShortenTextField: boolean) =>
    classNamesFunction<ITextFieldProps, ITextFieldStyles>()({
        root: { flex: 1 },
        field: { height: shouldShortenTextField ? 250 : 300, overflowY: 'auto' }
    });

export const DeployedModelUrlModal = (props: DeployedModelUrlModalProps) => {
    const {
        hidden,
        onSubmit,
        submitCode,
        predictUrl,
        onDismiss,
        retrieveCode,
        resourceOptions,
        selectedResource,
        isLoadingResources,
        onChangeSelectedResource,
        showDeploymentRegions = false
    } = props;
    const { localize } = useLocalization();
    const [selectedPivotKey, setSelectedPivotKey] = React.useState<'submit' | 'retrieve'>('submit');

    return (
        <BasicModal
            valid
            hidden={hidden}
            onSubmit={onSubmit}
            onDismiss={onDismiss}
            dialogContentProps={{
                title: localize(messages.getEndpointModalTitle),
                styles: { content: { whiteSpace: 'pre-line' } }
            }}
            onRenderFooter={() => (
                <PrimaryButton
                    onClick={onDismiss}
                    key="basicModalCancelBtnId"
                    text={localize(messages.close)}
                    data-automation-id="basicModalCancelBtnId"
                />
            )}>
            <p className={predictionURLParagraphClassName}>{localize(messages.getEndpointModelDesc)}</p>

            {showDeploymentRegions && (
                <LuisDropdown
                    options={resourceOptions}
                    selectedKey={selectedResource}
                    disabled={!resourceOptions.length}
                    styles={{ root: { maxWidth: 320, marginBottom: 5 } }}
                    placeholder={localize(messages.deploymentRegionPlaceholder)}
                    onChange={(_, opt) => onChangeSelectedResource(opt.key as string)}
                    onRenderLabel={() => <ModalSubTitle>{localize(messages.deploymentRegion)}</ModalSubTitle>}
                />
            )}

            <ModalSubTitle htmlFor="endpointURLTextField">{localize(messages.predictionURL)}</ModalSubTitle>

            {isLoadingResources ? (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.small} />
                    <LuisText>{localize(globalMessages.loading)}</LuisText>
                </Stack>
            ) : (
                <Stack horizontal horizontalAlign="center">
                    <LuisTextField
                        readOnly
                        value={predictUrl}
                        id="endpointURLTextField"
                        styles={{ root: { flex: 1 } }}
                        ariaLabel={localize(messages.predictionURL)}
                    />
                    <CopyButton textToCopy={predictUrl} label={localize(messages.predictionURL)} />
                </Stack>
            )}

            <SampleRequestSubTitle>{localize(messages.sampleRequest)}</SampleRequestSubTitle>

            {retrieveCode ? (
                <Pivot
                    selectedKey={selectedPivotKey}
                    onLinkClick={item => setSelectedPivotKey(item.props.itemKey as typeof selectedPivotKey)}>
                    <PivotItem itemKey="submit" headerText="Submit">
                        {isLoadingResources ? (
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                <Spinner size={SpinnerSize.small} />
                                <LuisText>{localize(globalMessages.loading)}</LuisText>
                            </Stack>
                        ) : (
                            <Stack styles={{ root: { marginTop: 8 } }} horizontal horizontalAlign="center">
                                <LuisTextField
                                    readOnly
                                    multiline
                                    resizable={false}
                                    value={submitCode}
                                    ariaLabel={localize(messages.submitCode)}
                                    styles={textFieldStyles(showDeploymentRegions)}
                                />
                                <CopyButton textToCopy={submitCode} label={localize(messages.submitCode)} />
                            </Stack>
                        )}
                    </PivotItem>

                    <PivotItem itemKey="retrieve" headerText="Retrieve">
                        {isLoadingResources ? (
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                <Spinner size={SpinnerSize.small} />
                                <LuisText>{localize(globalMessages.loading)}</LuisText>
                            </Stack>
                        ) : (
                            <Stack styles={{ root: { marginTop: 8 } }} horizontal horizontalAlign="center">
                                <LuisTextField
                                    readOnly
                                    multiline
                                    resizable={false}
                                    value={retrieveCode}
                                    ariaLabel={localize(messages.retrieveCode)}
                                    styles={textFieldStyles(showDeploymentRegions)}
                                />
                                <CopyButton textToCopy={retrieveCode} label={localize(messages.retrieveCode)} />
                            </Stack>
                        )}
                    </PivotItem>
                </Pivot>
            ) : isLoadingResources ? (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.small} />
                    <LuisText>{localize(globalMessages.loading)}</LuisText>
                </Stack>
            ) : (
                <Stack styles={{ root: { marginTop: 8 } }} horizontal horizontalAlign="center">
                    <LuisTextField
                        readOnly
                        multiline
                        resizable={false}
                        value={submitCode}
                        styles={textFieldStyles(false)}
                        ariaLabel={localize(messages.sampleRequest)}
                    />
                    <CopyButton textToCopy={submitCode} label={localize(messages.sampleRequest)} />
                </Stack>
            )}
        </BasicModal>
    );
};
